import { clickCiApiUrl } from './apiUrl';

export type LineageNodeType = 'source' | 'qvd' | 'app' | 'analytic';
export type LineageStage = 'source' | '00' | '01' | '02' | '03' | 'analytic';
export type LineageProvenance = 'observed' | 'parsed' | 'declared-demo';

export interface LineageEvidence {
  id: string;
  provider: 'qlik-lineage-api' | 'load-script' | 'engine' | 'reload-log';
  locator: string;
  observedAt: string;
  statement: string;
  excerpt: string;
  verification: 'verified' | 'declared-demo';
}

export interface LineageNode {
  id: string;
  type: LineageNodeType;
  stage: LineageStage;
  label: string;
  subtitle: string;
  model?: string;
  role?: 'selected' | 'shared-consumer';
  fields: string[];
  metadata: Record<string, string | number | boolean>;
  evidenceIds: string[];
  provenance?: LineageProvenance;
}

export interface LineageEdge {
  id: string;
  source: string;
  target: string;
  relation: 'extracts' | 'stores' | 'transforms' | 'loads' | 'consumes';
  fieldMappings: LineageFieldMapping[];
  evidenceIds: string[];
  provenance?: LineageProvenance;
}

export interface LineageFieldMapping {
  source: string;
  target: string;
  expression?: string;
  sourceFields?: string[];
}

export interface LineageStatus {
  snapshotId: string;
  generatedAt: string;
  digest: string;
  stale: boolean;
  mode: 'hybrid-demo';
  providerCoverage: Array<{ provider: LineageEvidence['provider']; state: 'observed' | 'partial' | 'simulated'; evidenceCount: number }>;
  scanSummary: {
    visibleApps: number; publishedApps: number; scannedApps: number; scriptsRead: number;
    appsWithNativeLineage: number; appsWithLoadedModel: number; modelTables: number;
    modelFields: number; nativeLineageItems: number; sharedConsumerResources: number;
    simulatedUpstreamNodes: number;
  };
}

export interface LineageScanStep {
  id: string;
  label: string;
  description: string;
  state: 'pending' | 'running' | 'succeeded' | 'failed';
  evidenceClass: 'observed' | 'declared-demo';
  result: string;
}

export interface LineageScanRun {
  id: string;
  status: 'running' | 'succeeded' | 'failed';
  site: string;
  publishedOnly: true;
  readOnly: true;
  environment: {
    product: 'Qlik Sense';
    deployment: 'Client-Managed';
    hosting: 'On-Premises';
  };
  startedAt: string;
  completedAt: string | null;
  activeStepId: string | null;
  steps: LineageScanStep[];
  summary: LineageStatus['scanSummary'] | null;
  snapshot: LineageStatus | null;
  error?: string;
}

export interface LineageAppSummary {
  id: string;
  name: string;
  model: string;
  qvdStages: number;
  loadedFields: number;
  analytics: number;
  sharedSource: boolean;
}

export interface LineageProjection {
  status: LineageStatus;
  mode: 'app-overview' | 'node-explore';
  anchorNodeId: string;
  selectedAppId: string | null;
  breadcrumb: Array<{ id: string; label: string }>;
  nodes: LineageNode[];
  edges: LineageEdge[];
  summary: { sources: number; qvdStages: number; fields: number; analytics: number };
}

export interface LineageFieldTrace {
  anchorNodeId: string;
  selectedField: string | null;
  tracedFields: string[];
  nodeIds: string[];
  edgeIds: string[];
  sourceNodeIds: string[];
  nodeFields: Record<string, string[]>;
  edgeMappings: Record<string, LineageFieldMapping[]>;
}

export interface LineageEvidenceConfidence {
  score: number;
  level: 'high' | 'medium' | 'low';
  label: 'Evidence confidence';
  methodology: string;
  drivers: string[];
  coverage: {
    claims: number; observedClaims: number; parsedClaims: number; simulatedClaims: number;
    verifiedEvidence: number; mappedEdges: number;
  };
}

export interface LineageImprovement {
  id: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  why: string;
  action: string;
  authority: 'deterministic-rule';
}

export interface LineageInterpretationReport {
  analysisId: string;
  snapshotId: string;
  generatedAt: string;
  anchor: { id: string; label: string; type: LineageNodeType };
  selectedField: string | null;
  tracedFields: string[];
  confidence: LineageEvidenceConfidence;
  narrative: { summary: string; basis: string; confidence: string; improvement: string };
  evidence: LineageEvidence[];
  evidenceReferences: string[];
  improvements: LineageImprovement[];
  authority: {
    status: 'generated' | 'fallback'; provider: 'openai' | 'deterministic'; model: string;
    promptVersion: 'lineage-evidence-v1'; responseId?: string; fallbackReason?: string;
  };
}

export type LineageInterpretationStreamEvent =
  | { type: 'meta'; data: Pick<LineageInterpretationReport, 'analysisId' | 'snapshotId' | 'generatedAt' | 'anchor' | 'selectedField' | 'tracedFields' | 'confidence' | 'authority'> }
  | { type: 'delta'; data: { section: keyof LineageInterpretationReport['narrative']; text: string } }
  | { type: 'evidence'; data: LineageEvidence }
  | { type: 'improvement'; data: LineageImprovement }
  | { type: 'done'; data: LineageInterpretationReport };

export interface LineageDemoScenario {
  id: string;
  title: string;
  customerGoal: string;
  presalesPersona: { name: string; role: string };
  clientPersona: { name: string; role: string };
  turns: Array<{ id: string; speaker: 'presales' | 'client' | 'system'; label: string; message: string; expectedAction?: string }>;
}

export interface LineageRehearsalFeedback {
  id: string;
  scenarioId: string;
  source: 'simulated-roleplay' | 'client-live';
  message: string;
  tags: string[];
  requestedVersion: string;
  status: 'candidate' | 'accepted' | 'implemented' | 'validated';
  createdAt: string;
}

interface ResultEnvelope<T> { result?: T; message?: string }
const apiUrl = clickCiApiUrl;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiUrl}${path}`, init);
  const payload = await response.json() as ResultEnvelope<T>;
  if (!response.ok || payload.result === undefined) throw new Error(payload.message ?? `Request failed (${response.status})`);
  return payload.result;
}

async function streamInterpretation(
  input: { nodeId: string; field?: string | null },
  onEvent: (event: LineageInterpretationStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${apiUrl}/api/demo-lineage/interpretations/stream`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input), signal,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as ResultEnvelope<never>;
    throw new Error(payload.message ?? `Interpretation stream failed (${response.status})`);
  }
  if (!response.body) throw new Error('Interpretation stream is not available in this browser.');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    lines.filter(Boolean).forEach(line => onEvent(JSON.parse(line) as LineageInterpretationStreamEvent));
    if (done) break;
  }
  if (buffer.trim()) onEvent(JSON.parse(buffer) as LineageInterpretationStreamEvent);
}

export const lineageDemoApi = {
  status: () => request<LineageStatus>('/api/demo-lineage/catalog/status'),
  listApps: (query = '') => request<LineageAppSummary[]>(`/api/demo-lineage/apps?q=${encodeURIComponent(query)}`),
  appOverview: (appId: string) => request<LineageProjection>(`/api/demo-lineage/apps/${encodeURIComponent(appId)}/overview`),
  explore: (nodeId: string, selectedAppId?: string | null) => request<LineageProjection>(`/api/demo-lineage/nodes/${encodeURIComponent(nodeId)}/explore${selectedAppId ? `?selectedAppId=${encodeURIComponent(selectedAppId)}` : ''}`),
  fieldTrace: (nodeId: string, field?: string | null) => request<LineageFieldTrace>(`/api/demo-lineage/nodes/${encodeURIComponent(nodeId)}/field-lineage${field ? `?field=${encodeURIComponent(field)}` : ''}`),
  node: (nodeId: string) => request<{ node: LineageNode; evidence: LineageEvidence[] }>(`/api/demo-lineage/nodes/${encodeURIComponent(nodeId)}`),
  edge: (edgeId: string) => request<{ edge: LineageEdge; evidence: LineageEvidence[] }>(`/api/demo-lineage/edges/${encodeURIComponent(edgeId)}/evidence`),
  rebuild: () => request<LineageStatus>('/api/demo-lineage/catalog/rebuild', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' }),
  startScan: () => request<LineageScanRun>('/api/demo-lineage/catalog/scans', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' }),
  scan: (scanId: string) => request<LineageScanRun>(`/api/demo-lineage/catalog/scans/${encodeURIComponent(scanId)}`),
  streamInterpretation,
  demoScenarios: () => request<LineageDemoScenario[]>('/api/demo-lineage/demo/scenarios'),
  rehearsalFeedback: () => request<LineageRehearsalFeedback[]>('/api/demo-lineage/demo/feedback'),
  submitRehearsalFeedback: (input: { scenarioId: string; source: LineageRehearsalFeedback['source']; message: string; tags: string[] }) => request<LineageRehearsalFeedback>('/api/demo-lineage/demo/feedback', {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input),
  }),
  updateRehearsalFeedback: (id: string, status: LineageRehearsalFeedback['status']) => request<LineageRehearsalFeedback>(`/api/demo-lineage/demo/feedback/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ status }),
  }),
};
